/**
 * NotificationDisplay - Shows collectible and file pickup notifications
 * Positioned in upper-right area, between health bar and clock
 */

import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../state/store';
import { removeNotification } from '../../state/slices/uiSlice';

export const NotificationDisplay: React.FC = () => {
  const dispatch = useDispatch();
  const notifications = useSelector((state: RootState) => state.ui.notifications);

  // Auto-remove notifications after their duration
  useEffect(() => {
    notifications.forEach((notification) => {
      const timeRemaining = notification.duration - (Date.now() - notification.timestamp);
      if (timeRemaining > 0) {
        const timer = setTimeout(() => {
          dispatch(removeNotification(notification.id));
        }, timeRemaining);
        return () => clearTimeout(timer);
      } else {
        dispatch(removeNotification(notification.id));
      }
    });
  }, [notifications, dispatch]);

  // Get icon based on notification type
  const getIcon = (type: string) => {
    switch (type) {
      case 'success': return '+';
      case 'info': return 'i';
      case 'warning': return '!';
      case 'error': return 'X';
      default: return '*';
    }
  };

  // Get colors based on notification type
  const getColors = (type: string) => {
    switch (type) {
      case 'success':
        return { border: '#00ff00', bg: 'rgba(0, 50, 0, 0.95)', text: '#00ff00', icon: '#00ff00' };
      case 'info':
        return { border: '#00aaff', bg: 'rgba(0, 30, 50, 0.95)', text: '#00aaff', icon: '#00aaff' };
      case 'warning':
        return { border: '#ffaa00', bg: 'rgba(50, 35, 0, 0.95)', text: '#ffaa00', icon: '#ffaa00' };
      case 'error':
        return { border: '#ff0000', bg: 'rgba(50, 0, 0, 0.95)', text: '#ff0000', icon: '#ff0000' };
      default:
        return { border: '#888', bg: 'rgba(30, 30, 30, 0.95)', text: '#fff', icon: '#888' };
    }
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div style={{
      position: 'absolute',
      top: '20px',
      left: '50%',
      transform: 'translateX(130px)', // Position to the right of the clock (clock is ~200px wide)
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      maxWidth: '260px',
      zIndex: 150,
      pointerEvents: 'none',
    }}>
      {notifications.slice(-4).map((notification, index) => {
        const colors = getColors(notification.type);
        const age = Date.now() - notification.timestamp;
        const fadeStart = notification.duration - 500; // Start fading 500ms before removal
        const opacity = age > fadeStart ? Math.max(0, 1 - (age - fadeStart) / 500) : 1;

        return (
          <div
            key={notification.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 14px',
              backgroundColor: colors.bg,
              border: `2px solid ${colors.border}`,
              borderRadius: '6px',
              fontFamily: 'monospace',
              fontSize: '13px',
              color: colors.text,
              boxShadow: `0 0 15px ${colors.border}40, inset 0 0 20px ${colors.border}10`,
              opacity: opacity,
              animation: 'slideIn 0.3s cubic-bezier(0.22,1,0.36,1) forwards, notifPulse 0.5s ease-out 0.3s',
            }}
          >
            {/* Icon */}
            <div style={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colors.icon,
              color: '#000',
              borderRadius: '4px',
              fontWeight: 'bold',
              fontSize: '14px',
              flexShrink: 0,
            }}>
              {getIcon(notification.type)}
            </div>

            {/* Message */}
            <div style={{
              flex: 1,
              lineHeight: '1.3',
              textShadow: `0 0 10px ${colors.text}80`,
            }}>
              {notification.message}
            </div>
          </div>
        );
      })}

      <style>
        {`
          @keyframes slideIn {
            from { opacity: 0; transform: translateX(60px) scale(0.92); }
            to   { opacity: 1; transform: translateX(0)   scale(1); }
          }
          @keyframes notifPulse {
            0%   { box-shadow: 0 0 15px var(--clr, #fff)40, inset 0 0 20px var(--clr, #fff)10; }
            40%  { box-shadow: 0 0 28px var(--clr, #fff)80, inset 0 0 24px var(--clr, #fff)20; }
            100% { box-shadow: 0 0 15px var(--clr, #fff)40, inset 0 0 20px var(--clr, #fff)10; }
          }
        `}
      </style>
    </div>
  );
};
